import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_SESSION_EXPIRY_WARNING_MS = 10 * 60 * 1000;
const SESSION_EXPIRY_CLOCK_SKEW_MS = 30 * 1000;

const getSessionExpiryWarningMs = () => {
  const parsedValue = Number(process.env.REACT_APP_SESSION_EXPIRY_WARNING);
  return Number.isFinite(parsedValue) && parsedValue > 0
    ? parsedValue
    : DEFAULT_SESSION_EXPIRY_WARNING_MS;
};

const clearTimer = (timerRef) => {
  if (timerRef.current) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
};

// setTimeout delays are a 32-bit signed int internally — anything beyond this
// overflows and fires almost immediately instead of waiting. A long-lived
// token (e.g. SSO/"remember me") could otherwise trigger an instant false
// "session expired" the moment it's scheduled. Chunk longer delays into
// MAX_TIMEOUT_DELAY-sized waits instead of scheduling one oversized timer.
const MAX_TIMEOUT_DELAY = 2147483647; // ~24.8 days

const scheduleTimeout = (timerRef, delay, callback) => {
  const clamped = Math.max(delay, 0);
  if (clamped > MAX_TIMEOUT_DELAY) {
    timerRef.current = setTimeout(
      () => scheduleTimeout(timerRef, clamped - MAX_TIMEOUT_DELAY, callback),
      MAX_TIMEOUT_DELAY
    );
  } else {
    timerRef.current = setTimeout(callback, clamped);
  }
};

const useSessionExpiryTimer = ({ accessToken, onExpire, decodeAccessToken }) => {
  const [showSessionModal, setShowSessionModal] = useState(false);
  const warningTimerRef = useRef(null);
  const expiryTimerRef = useRef(null);
  const warningMs = getSessionExpiryWarningMs();

  const clearTimers = useCallback(() => {
    clearTimer(warningTimerRef);
    clearTimer(expiryTimerRef);
  }, []);

  const expireSession = useCallback(() => {
    clearTimers();
    setShowSessionModal(false);
    onExpire?.();
  }, [clearTimers, onExpire]);

  const evaluateSession = useCallback(() => {
    clearTimers();

    if (!accessToken) {
      setShowSessionModal(false);
      return;
    }

    const decodedToken = decodeAccessToken?.(accessToken);
    const exp = Number(decodedToken?.exp);

    if (!Number.isFinite(exp)) {
      console.warn('[SessionChecker] Access token is malformed or missing exp; logging out.');
      expireSession();
      return;
    }

    const now = Date.now();
    const expiryTime = exp * 1000;
    const timeLeft = expiryTime - now;

    if (timeLeft <= SESSION_EXPIRY_CLOCK_SKEW_MS) {
      expireSession();
      return;
    }

    const warningDelay = expiryTime - warningMs - now;
    if (warningDelay <= 0) {
      setShowSessionModal(true);
    } else {
      scheduleTimeout(warningTimerRef, warningDelay, () => setShowSessionModal(true));
    }

    scheduleTimeout(expiryTimerRef, timeLeft - SESSION_EXPIRY_CLOCK_SKEW_MS, expireSession);
  }, [accessToken, decodeAccessToken, expireSession, warningMs, clearTimers]);

  useEffect(() => {
    evaluateSession();
    return clearTimers;
  }, [evaluateSession, clearTimers]);

  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        evaluateSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
    };
  }, [evaluateSession]);

  return {
    showSessionModal,
    setShowSessionModal,
    warningMs,
  };
};

export default useSessionExpiryTimer;
